import { CoachCardView, CellTone, OnboardingCell } from './coachOnboarding';
import { CoachActionState, quietLineOf, LOCAL_ONLY_NOTE } from './coachOnboardingActions';
import { MemberNeed, MemberActionTarget, NEED_HINT } from './coachOnboardingNav';

/**
 * 강사별 온보딩 카드 — 시안 `OPS_BRIEFING_ONBOARDING_UI.html` 의 `.bc` 를 옮긴 것.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 파일은 **그리기만** 한다
 * ─────────────────────────────────────────────────────────────
 * 헤드라인·잠금·색·배지는 전부 `coachOnboarding.toCardView` 가 정한다.
 * 여기서 다시 판정하면 화면·팝업·카운터가 갈린다 (QA34 에서 실제로 그랬다).
 *
 * ─────────────────────────────────────────────────────────────
 * 시안과 다르게 간 곳 (대표 확정)
 * ─────────────────────────────────────────────────────────────
 * · 「회원 N명이 기다립니다」 배지 없음 — 「기다린다」는 상태가 데이터에 없다.
 * · 「담당 예정」 칸 없음 — 언제나 「담당 회원」.
 * 둘 다 `coachOnboarding` 에서 이미 안 만든다. 이 파일은 안 그릴 뿐이다.
 */

const LINE = '#C9D9EE';
const CELL_BG = '#F7F9FC';
const CELL_LINE = '#EDF1F7';
const INK = '#16202E';
const MUTE = '#7C8794';

/** 카드 왼쪽 레일 — 성격에 따라 색이 갈린다 (시안 `.bc.blue/.amberc/.ok`) */
const RAIL: Record<'coach' | 'owner' | 'clear', string> = {
    coach: '#E8A93C',
    owner: '#4A79D9',
    clear: '#3EBD8B',
};

const TAG: Record<'coach' | 'owner' | 'clear', { bg: string; fg: string }> = {
    coach: { bg: '#FCF3E0', fg: '#9A6A00' },
    owner: { bg: '#DCE7F8', fg: '#2C4C8C' },
    clear: { bg: '#E6F5EF', fg: '#0E7C58' },
};

const CELL_TONE: Record<CellTone, { bg: string; fg: string; sub: string }> = {
    plain: { bg: CELL_BG, fg: INK, sub: MUTE },
    good: { bg: CELL_BG, fg: '#0E7C58', sub: MUTE },
    warn: { bg: '#FCF7EA', fg: '#9A6A00', sub: '#9A6A00' },
    hot: { bg: '#FDF3F3', fg: '#D22730', sub: '#D22730' },
    /* 잠긴 칸·못 잰 칸 — 값이 아니라 상태를 적는 자리라 글씨가 작고 흐리다 */
    off: { bg: '#F3F5F9', fg: '#A8B3C4', sub: '#A8B3C4' },
};

const Cell = ({ c }: { c: OnboardingCell }) => {
    const t = CELL_TONE[c.tone];
    return (
        <div
            /*
              🔴 시안 `.cell` 그대로 — `flex:1` · `min-width:0`.
                 1100px 미만에서만 `basis 32%` 로 3+3 이 된다.
                 예전에는 `flex:1 1 32%` 를 **모든 폭에** 줘서, 1900px 화면에서도
                 여섯 칸이 두 줄로 접혔다(32%×6 = 192% > 100%).
            */
            className="flex min-w-0 flex-1 flex-col items-center max-[1100px]:basis-[32%]"
            style={{ background: t.bg, padding: '11px 8px' }}
        >
            {/*
              칸이 좁아지면 **줄바꿈이 아니라 말줄임**이다. 한 줄 유지가 우선이라
              「일지 · 최근 7일」처럼 긴 라벨도 칸을 밀어내지 않는다.
              전체 문구는 title 로 남긴다 — 잘렸다고 뜻을 잃으면 안 된다.
            */}
            <span
                className="w-full overflow-hidden text-ellipsis"
                title={c.title ?? c.k}
                style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: c.tone === 'off' ? '#A8B3C4' : MUTE,
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                }}
            >
                {c.k}
            </span>
            <span
                className="flex max-w-full items-baseline justify-center overflow-hidden"
                style={{
                    gap: 2,
                    marginTop: 4,
                    /* 잠긴 칸은 값이 아니라 문장이라 크게 쓰지 않는다 (시안 `.cell.off`) */
                    fontSize: c.tone === 'off' ? 13 : 18,
                    fontWeight: 800,
                    color: t.fg,
                    letterSpacing: '-0.3px',
                    whiteSpace: 'nowrap',
                }}
            >
                {c.v}
                {c.unit && <span style={{ fontSize: 12, fontWeight: 700, color: MUTE }}>{c.unit}</span>}
            </span>
            {c.s && (
                <span style={{ fontSize: 11, fontWeight: 700, color: t.sub, marginTop: 2, whiteSpace: 'nowrap' }}>
                    {c.s}
                </span>
            )}
        </div>
    );
};

/** 회원 줄의 버튼 — 「무엇이 필요한가」 (최대 2개, 서버가 우선순위 정렬) */
const NEED_LABEL: Record<string, { label: string; tone: 'red' | 'amber' | 'grey' }> = {
    PASS: { label: '수강권 등록', tone: 'red' },
    BOOKING: { label: '스케줄 잡기', tone: 'red' },
    APP: { label: '앱 초대 보내기', tone: 'amber' },
    JOURNAL: { label: '일지 작성 요청', tone: 'amber' },
    /* 🔴 신규기회 전용 — 회색이다. 독촉 톤 금지 (대표 확정 ⓓ) */
    SUGGEST_PASS: { label: '수강권 제안하기', tone: 'grey' },
};

const NEED_TONE = {
    red: { bg: '#FDECEC', fg: '#D22730' },
    amber: { bg: '#FCF3E0', fg: '#9A6A00' },
    grey: { bg: '#EDF1F7', fg: '#5B6B7F' },
};

type Props = {
    view: CoachCardView;
    /** 화면에 몇 번째로 서는가 — 회원 카드와 이어지는 번호 */
    index: number;
    action: CoachActionState;
    /** 서버에 이력이 남는가 — 아니면 한계를 밝힌다 */
    serverBacked: boolean;
    onCheck: (coachId: string) => void;
    onHide: (coachId: string) => void;
    /**
     * 회원 줄 버튼 — 갈 데가 있는 것만 버튼이다 (`coachOnboardingNav`).
     * 갈 데가 없는 것(앱 초대·일지 요청)은 버튼이 아니라 안내 문구로 나간다.
     */
    onOpenMember?: (need: MemberNeed, target: MemberActionTarget) => void;
};

const CoachOnboardingCard = ({
    view,
    index,
    action,
    serverBacked,
    onCheck,
    onHide,
    onOpenMember,
}: Props) => {
    const tone = view.actor.tone;
    const quietLine = quietLineOf(action);
    const noteSuffix = serverBacked ? '' : ` · ${LOCAL_ONLY_NOTE}`;

    return (
        <div
            className="flex w-full overflow-hidden rounded-[16px] border border-solid"
            style={{
                borderColor: LINE,
                background: '#FFFFFF',
                marginBottom: 14,
                /* 조용한 모드는 **눈에 띄게 물러난다** — 이게 「조용히」의 뜻이다 */
                opacity: action.quiet ? 0.72 : 1,
            }}
            aria-label={`강사 온보딩 · ${view.row.name}`}
        >
            <div style={{ width: 6, flex: 'none', background: RAIL[tone] }} />

            {/* 번호 + 체크 — 회원 카드와 같은 자리 */}
            <div className="flex flex-none flex-col items-center" style={{ width: 52, paddingTop: 18 }}>
                {view.chore ? (
                    <button
                        type="button"
                        onClick={() => onCheck(view.row.coachId)}
                        title={`오늘 연락했다고 표시합니다 · 사흘간 조용히 둡니다${noteSuffix}`}
                        aria-label={`${view.row.name} 강사 오늘 연락함으로 표시`}
                        className="flex items-center justify-center"
                        style={{
                            width: 24,
                            height: 24,
                            borderRadius: 8,
                            border: action.quiet ? '1.5px solid #0E7C58' : `1.5px solid ${LINE}`,
                            background: action.quiet ? '#0E7C58' : '#FFFFFF',
                            color: '#FFFFFF',
                            fontSize: 14,
                            fontWeight: 800,
                            cursor: 'pointer',
                            padding: 0,
                        }}
                    >
                        {action.quiet ? '✓' : ''}
                    </button>
                ) : null}
                <span
                    aria-label={view.chore ? `${index + 1}번째 카드` : undefined}
                    style={{ fontSize: 12, fontWeight: 700, color: MUTE, marginTop: view.chore ? 8 : 4 }}
                >
                    {view.chore ? index + 1 : '—'}
                </span>
            </div>

            <div className="flex min-w-0 flex-1 flex-col" style={{ padding: '18px 20px' }}>
                {/* 머리 줄 */}
                <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                    <span
                        className="rounded-[6px]"
                        style={{
                            background: TAG[tone].bg,
                            color: TAG[tone].fg,
                            fontSize: 12.5,
                            fontWeight: 800,
                            padding: '4px 10px',
                            flex: 'none',
                        }}
                    >
                        {view.actor.label}
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: INK }}>
                        {view.row.name} 강사
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: MUTE, wordBreak: 'keep-all' }}>
                        {subLineOf(view)}
                    </span>
                    <span style={{ flex: 1 }} />
                    {view.badge && (
                        <span
                            className="rounded-[8px]"
                            style={{
                                background: '#FDECEC',
                                color: '#D22730',
                                fontSize: 13.5,
                                fontWeight: 800,
                                padding: '6px 12px',
                                flex: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {view.badge}
                        </span>
                    )}
                    {view.chore && (
                        <button
                            type="button"
                            onClick={() => onHide(view.row.coachId)}
                            title={`오늘 하루만 숨깁니다 · 내일 아침 다시 보입니다${noteSuffix}`}
                            aria-label={`${view.row.name} 강사 오늘만 가리기`}
                            className="rounded-[7px]"
                            style={{
                                height: 30,
                                padding: '0 12px',
                                background: '#FFFFFF',
                                border: `1px solid ${LINE}`,
                                color: MUTE,
                                fontSize: 12.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                                flex: 'none',
                            }}
                        >
                            오늘만 가리기
                        </button>
                    )}
                </div>

                {/*
                  어제 진전 — **서버가 줄 때만** (대표 확정 ⓑ).
                  화면은 어제 값을 들고 있지 않으므로 지어내지 않는다.
                */}
                {view.progressNote && (
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 10,
                            background: '#E6F5EF',
                            color: '#0E7C58',
                            fontSize: 13,
                            fontWeight: 700,
                            padding: '8px 13px',
                        }}
                    >
                        {view.progressNote}
                    </div>
                )}

                {/*
                  🔴 조용한 모드면 **헤드라인을 덮는다.**
                     독촉 문장을 그대로 두고 작은 글씨만 더하면 조용해진 게 아니다.
                */}
                {view.chore &&
                    (quietLine ? (
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0E7C58', marginTop: 11 }}>
                            ✓ {quietLine} — 사흘 뒤에 다시 확인하겠습니다
                        </div>
                    ) : (
                        <div
                            style={{
                                fontSize: 19,
                                fontWeight: 800,
                                color: INK,
                                letterSpacing: '-0.35px',
                                marginTop: 11,
                                wordBreak: 'keep-all',
                            }}
                        >
                            {action.quietExpired ? '연락 후에도 그대로입니다 — ' : ''}
                            {view.headline}
                        </div>
                    ))}

                {/* 🔓 해금 안내 — 세팅은 「무엇이 열리나」를 붙여야 움직인다 */}
                {view.chore && view.unlockNote && !quietLine && (
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 12,
                            background: '#E6F5EF',
                            color: '#0E7C58',
                            fontSize: 13,
                            fontWeight: 700,
                            padding: '9px 13px',
                            wordBreak: 'keep-all',
                        }}
                    >
                        🔓 {view.unlockNote}
                    </div>
                )}

                {/* 셀 여섯 칸 */}
                {/*
                  🔴 시안 `.cells` 그대로 — 기본은 **한 줄(nowrap)**,
                     1100px 미만에서만 접어서 3+3 이 된다.
                */}
                <div
                    className="flex flex-nowrap overflow-hidden rounded-[12px] max-[1100px]:flex-wrap"
                    style={{ gap: 1, background: CELL_LINE, border: `1px solid ${CELL_LINE}`, marginTop: 14 }}
                >
                    {view.cells.map((c) => (
                        <Cell key={c.k} c={c} />
                    ))}
                </div>

                {/* 회원 줄 — 그 회원에게 필요한 것 최대 2개 */}
                {view.row.memberIssues.length > 0 && (
                    <div
                        className="overflow-hidden rounded-[12px]"
                        style={{ border: `1px solid ${CELL_LINE}`, marginTop: 12 }}
                    >
                        {view.row.memberIssues.map((m, i) => {
                            const opportunity = m.kind === 'opportunity';
                            return (
                                <div
                                    key={m.memberId}
                                    className="flex flex-wrap items-center"
                                    style={{
                                        gap: 12,
                                        padding: '10px 16px',
                                        borderTop: i === 0 ? 'none' : `1px solid ${CELL_LINE}`,
                                        /* 신규기회는 배경부터 물러난다 — 독촉 대상이 아니다 */
                                        background: opportunity ? '#FAFBFD' : '#FFFFFF',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            color: INK,
                                            width: 88,
                                            flex: 'none',
                                        }}
                                    >
                                        {m.name}
                                    </span>
                                    {/*
                                      🔴 **다른 강사에게 이미 수강권이 있는 회원** (2026-09-02).

                                      한 회원이 여러 강사에게 배정되는 것은 정식 기능이고,
                                      수강권은 강사 귀속으로 세기 때문에 이 카드에서는
                                      「없음」으로 잡히는 것이 맞다. 틀린 것은 **여기서
                                      등록을 권하는 것**이다 — 누르면 중복 발급이 된다.

                                      톤은 중성이다. 붉은 독촉이 아니라 사실 서술이라,
                                      원장님이 「이건 처리할 일이 아니구나」로 읽어야 한다.
                                    */}
                                    {m.passHeldBy && (
                                        <span
                                            className="rounded-[6px]"
                                            title={`${m.passHeldBy.name} 강사에게 활성 수강권이 있습니다 · 이 카드에서는 담당 강사가 달라 「없음」으로 잡힙니다`}
                                            style={{
                                                background: '#F3F5F9',
                                                color: '#5B6B7F',
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                padding: '3px 8px',
                                                flex: 'none',
                                                whiteSpace: 'nowrap',
                                                cursor: 'help',
                                            }}
                                        >
                                            {m.passHeldBy.name} 강사에게 수강권 있음
                                        </span>
                                    )}
                                    <span
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: MUTE,
                                            flex: 1,
                                            minWidth: 160,
                                            wordBreak: 'keep-all',
                                        }}
                                    >
                                        {m.note}
                                        {/*
                                          🔴 신규기회는 **왜 정상인지**를 화면이 말한다
                                             (대표 확정 ⓓ). 안 적으면 바로 위 줄들과
                                             똑같이 「수강권 없음」으로 읽혀서, 원장님이
                                             아직 등록도 안 한 체험 회원에게 독촉한다.
                                        */}
                                        {opportunity && ' · 위 회원들과 다릅니다'}
                                    </span>
                                    {/*
                                      🔴 다른 강사에게 수강권이 있으면 **등록 계열 버튼을
                                         통째로 뺀다.** 문구로 경고하는 것으로는 부족하다 —
                                         누를 수 있으면 눌리고, 그 순간 중복 발급이다.
                                         나머지 필요(앱·예약·일지)는 여전히 유효하므로 남긴다.
                                    */}
                                    {(m.passHeldBy
                                        ? m.needs.filter((n) => n !== 'PASS' && n !== 'SUGGEST_PASS')
                                        : m.needs
                                    )
                                        .slice(0, 2)
                                        .map((need) => {
                                        const spec = NEED_LABEL[need];
                                        if (!spec) return null;
                                        const t = NEED_TONE[spec.tone];
                                        const hint = NEED_HINT[need as MemberNeed];
                                        /*
                                          🔴 **갈 데가 없으면 버튼을 만들지 않는다.**
                                             눌러도 아무 일이 없는 버튼은 화면 전체를
                                             못 믿게 만든다. 대신 지금 할 수 있는 일을
                                             적어 준다 (`NEED_HINT`).
                                        */
                                        if (hint) {
                                            return (
                                                <span
                                                    key={need}
                                                    title={hint}
                                                    aria-label={`${m.name} ${spec.label} — ${hint}`}
                                                    className="rounded-[8px]"
                                                    style={{
                                                        height: 29,
                                                        lineHeight: '29px',
                                                        padding: '0 12px',
                                                        background: '#F3F5F9',
                                                        color: '#7C8794',
                                                        fontSize: 12.5,
                                                        fontWeight: 700,
                                                        flex: 'none',
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                    }}
                                                >
                                                    {spec.label}
                                                </span>
                                            );
                                        }
                                        return (
                                            <button
                                                key={need}
                                                type="button"
                                                onClick={() =>
                                                    onOpenMember?.(need as MemberNeed, {
                                                        memberId: m.memberId,
                                                        memberName: m.name,
                                                        coachId: view.row.coachId,
                                                        coachName: view.row.name,
                                                    })
                                                }
                                                aria-label={`${m.name} ${spec.label}`}
                                                className="rounded-[8px]"
                                                style={{
                                                    height: 29,
                                                    padding: '0 12px',
                                                    border: 'none',
                                                    background: t.bg,
                                                    color: t.fg,
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    cursor: 'pointer',
                                                    flex: 'none',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {spec.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

/** 이름 옆 작은 줄 — 「8/21 수락 · 담당 4명 · 신규기회 1명」 */
const subLineOf = (view: CoachCardView): string => {
    const r = view.row;
    const parts: string[] = [];
    if (r.isConnected && r.connectedOn) parts.push(`${r.connectedOn.slice(5).replace('-', '/')} 수락`);
    else parts.push(`${r.registeredOn.slice(5).replace('-', '/')} 등록`);
    parts.push(`담당 ${r.memberCount}명`);
    if (r.opportunityCount > 0) parts.push(`신규기회 ${r.opportunityCount}명`);
    /* 연락처는 **수락 독촉 중일 때만** 낸다 — 늘 띄우면 그냥 개인정보다 */
    if (!r.isConnected && r.phone && view.tier >= 2) parts.push(r.phone);
    return parts.join(' · ');
};

export default CoachOnboardingCard;
